import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Image as ImageIcon, Video, Grid3X3 } from 'lucide-react';
import { aiCreatives as mockCreatives } from '../data/mock';
import { aiCreativesFaqs } from '../data/faqs';
import FAQ from '../components/FAQ';
import useSEO from '../hooks/useSEO';
import { ROUTE_DEFAULTS, SITE_URL } from '../config/seo';

// Descriptive alt text derived from gallery item metadata.
// Keeps every image captioned for screen readers and AI crawlers — never a
// bare filename, never empty, never just the category slug.
const buildAlt = (item) => {
  if (!item) return 'AI-generated creative by Cultiv AI Studio';
  const title = item.title && item.title.trim() ? item.title.trim() : 'Creative';
  const category = item.category && item.category.trim() ? item.category.trim() : 'AI creative';
  const media = item.type === 'video' ? 'video' : 'image';
  return `${title} — AI-generated ${category} ${media} by Cultiv AI Studio`;
};

// Short, citable descriptions for the major gallery categories.
// Rendered as a stacked header strip above the grid so the page works for
// both human scanners and AI search engines that pull passage-level context.
const CATEGORY_DESCRIPTIONS = [
  {
    key: 'ai-videos',
    name: 'AI Videos',
    description:
      'AI-generated short-form video, reels, and brand films. Produced with Runway, Sora, Kling, and Google Veo, then edited with native platform pacing for Meta, TikTok, and YouTube. Used for campaign launches, social content, and testing dozens of hooks per week without a shoot day.',
  },
  {
    key: 'ai-portraits',
    name: 'AI Portraits',
    description:
      'Stylized and photorealistic portrait work — AI models, lifestyle talent, character studies, and creator-style UGC faces. Built with Midjourney, Stable Diffusion, and Flux, with a human art director curating composition, lighting, and expression so every frame reads as intentional, premium, and on-brand.',
  },
  {
    key: 'ai-art',
    name: 'AI Art & Design',
    description:
      'Editorial illustrations, posters, wallpapers, isometrics, and concept pieces. Broad-style experimentation across Midjourney and Gemini, used for creative direction, mood-boarding, and bold hero imagery across web, print, and social campaigns.',
  },
  {
    key: 'branding',
    name: 'Branding & Identity',
    description:
      'AI-generated brand mockups, logo applications, luxury identity systems, and packaging looks. Blends generative imagery with traditional brand design so launch-ready brand assets ship in days instead of weeks, at a fraction of traditional studio cost.',
  },
  {
    key: 'gemini',
    name: 'Gemini AI Visuals',
    description:
      "Work produced with Google Gemini's Imagen and Veo models. High-resolution, photoreal scenes used for hero imagery, product renders, and campaign storytelling where photorealism and tight brand color control matter most.",
  },
  {
    key: 'product-ads',
    name: 'Product & Ads',
    description:
      'Photoreal product renders, perfume and watch ads, shoe and accessory shots, and UGC-style commercial creatives. Replaces traditional product photography for e-commerce, Amazon PDPs, and paid social at around 90% cost savings versus studio production.',
  },
];

// ─── Lazy Image Component (Intersection Observer) ──────────────────────────────
const LazyImage = ({ src, alt, className, onClick, eager }) => {
  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [inView, setInView] = useState(eager || false);

  useEffect(() => {
    if (eager) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { rootMargin: '200px' }
    );
    if (imgRef.current) observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, [eager]);

  return (
    <div ref={imgRef} className={className} onClick={onClick} style={{ cursor: 'pointer' }}>
      {/* Skeleton shimmer */}
      {!loaded && (
        <div className="absolute inset-0 bg-white/5 animate-pulse rounded-xl" />
      )}
      {inView && (
        <img
          src={src}
          alt={alt}
          width={1200}
          height={800}
          loading={eager ? 'eager' : 'lazy'}
          decoding="async"
          fetchPriority={eager ? 'high' : 'auto'}
          onLoad={() => setLoaded(true)}
          onError={(e) => { e.target.style.display = 'none'; }}
          className={`w-full h-full object-cover transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'}`}
        />
      )}
    </div>
  );
};

// ─── Lazy Video Component ───────────────────────────────────────────────────────
const LazyVideo = ({ src, onClick, eager }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [inView, setInView] = useState(eager || false);

  useEffect(() => {
    if (eager) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setInView(true); observer.disconnect(); } },
      { rootMargin: '300px' }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, [eager]);

  return (
    <div ref={containerRef} className="relative w-full h-full" onClick={onClick} style={{ cursor: 'pointer' }}>
      {inView ? (
        <video
          ref={videoRef}
          src={src}
          autoPlay
          muted
          loop
          playsInline
          className="w-full h-full object-cover"
        />
      ) : (
        <div className="absolute inset-0 bg-white/5 animate-pulse rounded-xl" />
      )}
      {/* Play indicator */}
      <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
        <div className="w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center border border-white/20">
          <Play size={18} fill="white" className="text-white ml-1" />
        </div>
      </div>
    </div>
  );
};

// ─── Lightbox ──────────────────────────────────────────────────────────────────
const Lightbox = ({ item, onClose }) => {
  useEffect(() => {
    const handleKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.25 }}
        className="fixed inset-0 z-[100] flex items-center justify-center bg-black/95 backdrop-blur-lg"
        onClick={onClose}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-6 right-6 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors duration-200 z-10"
        >
          <X size={20} className="text-white" />
        </button>

        {/* Info label */}
        <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center">
          <p className="text-white text-sm font-light tracking-wide">{item.category}</p>
        </div>

        {/* Media */}
        <motion.div
          initial={{ scale: 0.92, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.92, opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="max-w-[90vw] max-h-[85vh] flex items-center justify-center"
          onClick={(e) => e.stopPropagation()}
        >
          {item.type === 'image' ? (
            <img
              src={item.src}
              alt={buildAlt(item)}
              width={1600}
              height={900}
              loading="eager"
              decoding="async"
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
          ) : (
            <video
              src={item.src}
              controls
              autoPlay
              className="max-w-full max-h-[85vh] rounded-lg shadow-2xl"
            />
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// ─── Masonry Item ──────────────────────────────────────────────────────────────
const MasonryItem = ({ item, index, onSelect }) => {
  const eager = index < 8;
  const [hovered, setHovered] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '0px 0px -50px 0px' }}
      transition={{ duration: 0.5, delay: (index % 8) * 0.06, ease: 'easeOut' }}
      className="relative mb-4 break-inside-avoid rounded-xl overflow-hidden group"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onSelect(item)}
      style={{ cursor: 'pointer' }}
    >
      {/* Media */}
      {item.type === 'image' ? (
        <div className="relative w-full">
          <LazyImage
            src={item.src}
            alt={buildAlt(item)}
            className="relative w-full block rounded-xl overflow-hidden"
            eager={eager}
          />
        </div>
      ) : (
        <div className="relative w-full aspect-video rounded-xl overflow-hidden">
          <LazyVideo src={item.src} onClick={() => onSelect(item)} eager={eager} />
        </div>
      )}

      {/* Hover overlay */}
      <motion.div
        initial={false}
        animate={{ opacity: hovered ? 1 : 0 }}
        transition={{ duration: 0.25 }}
        className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent rounded-xl flex flex-col justify-end p-4 pointer-events-none"
      >
        <p className="text-white text-sm font-light leading-tight">{item.category}</p>
      </motion.div>

      {/* Video badge */}
      {item.type === 'video' && (
        <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1 flex items-center gap-1 pointer-events-none">
          <Play size={10} fill="white" className="text-white" />
          <span className="text-white text-[10px] font-medium">Video</span>
        </div>
      )}
    </motion.div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────
const FILTERS = ['All', 'Images', 'Videos'];

const API_BASE = process.env.REACT_APP_API_URL || '';

const AICreatives = () => {
  useSEO({
    ...ROUTE_DEFAULTS['/ai-creatives'],
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      '@id': `${SITE_URL}/ai-creatives#webpage`,
      url: `${SITE_URL}/ai-creatives`,
      name: 'AI Creatives Gallery — Cultiv AI Studio',
      description:
        'Live gallery of AI-generated creatives from Cultiv AI Studio — videos, portraits, brand designs, 3D renders, and Gemini visuals.',
      isPartOf: { '@id': `${SITE_URL}/#website` },
      about: { '@id': `${SITE_URL}/#organization` },
      hasPart: CATEGORY_DESCRIPTIONS.map((cat) => ({
        '@type': 'CreativeWorkSeries',
        name: cat.name,
        description: cat.description,
        url: `${SITE_URL}/ai-creatives#${cat.key}`,
      })),
    },
  });

  const [activeFilter, setActiveFilter] = useState('All');
  const [selectedItem, setSelectedItem] = useState(null);
  const [creatives, setCreatives] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/api/creatives`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => {
        if (Array.isArray(data) && data.length > 0) {
          setCreatives(data);
        } else {
          setCreatives(mockCreatives);
        }
      })
      .catch(() => { setCreatives(mockCreatives); })
      .finally(() => { setLoading(false); });
  }, []);

  const filteredCreatives = creatives.filter((item) => {
    if (activeFilter === 'Images') return item.type === 'image';
    if (activeFilter === 'Videos') return item.type === 'video';
    return true;
  });

  const handleSelect = useCallback((item) => setSelectedItem(item), []);
  const handleClose = useCallback(() => setSelectedItem(null), []);

  return (
    <div className="bg-black text-white min-h-screen pt-32 pb-20">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">

        {/* ── Hero Header ──────────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="mb-16 text-center"
        >
          <h1 className="md:text-8xl text-6xl !font-light mb-6 tracking-tight">
            AI Creatives
          </h1>
          <p className="text-xl text-white/60 max-w-2xl mx-auto">
            A living gallery of AI-generated images, ads, portraits, and brand visuals—
            produced without a single camera.
          </p>
        </motion.div>

        {/* ── Intro / About this gallery ───────────────────────────────────── */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          aria-labelledby="creatives-about-heading"
          className="max-w-3xl mx-auto mb-16 text-center"
        >
          <h2
            id="creatives-about-heading"
            className="text-2xl md:text-3xl font-light tracking-tight mb-5 text-white"
          >
            About this gallery
          </h2>
          <div className="space-y-4 text-base md:text-lg text-white/70 leading-relaxed text-left md:text-center">
            <p>
              The AI creatives gallery at Cultiv AI Studio is a living showcase of
              generative work produced for clients and portfolio campaigns — AI-generated
              videos, portraits, brand systems, 3D product renders, and Gemini visuals
              built without a single camera or location shoot.
            </p>
            <p>
              Every asset combines senior human creative direction with a production
              stack of Midjourney, Google Gemini (Imagen and Veo), Runway, Kling, Stable
              Diffusion with LoRA fine-tunes, and Flux, finished in Photoshop for detail
              work. The AI handles volume and iteration; our art directors handle taste,
              brand, and story.
            </p>
            <p>
              Filter the grid by images or videos, click any asset to view it
              full-screen, and scroll to the FAQ for licensing and commission details.
              To brief similar work for your brand, book a free strategy call from the
              contact page.
            </p>
          </div>
        </motion.section>

        {/* ── Category descriptions strip ──────────────────────────────────── */}
        <motion.section
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.6, ease: 'easeOut', delay: 0.1 }}
          aria-labelledby="creatives-categories-heading"
          className="mb-16"
        >
          <h2
            id="creatives-categories-heading"
            className="text-2xl md:text-3xl font-light tracking-tight mb-8 text-white text-center"
          >
            What you'll find in the gallery
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {CATEGORY_DESCRIPTIONS.map((cat) => (
              <article
                key={cat.key}
                id={cat.key}
                className="rounded-xl border border-white/10 bg-white/5 p-5 hover:border-white/20 transition-colors"
              >
                <h3 className="text-lg font-medium text-white mb-2">{cat.name}</h3>
                <p className="text-sm text-white/65 leading-relaxed">{cat.description}</p>
              </article>
            ))}
          </div>
        </motion.section>

        {/* ── Filter Bar ───────────────────────────────────────────────────── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="flex items-center justify-center gap-3 mb-12 flex-wrap"
        >
          {FILTERS.map((filter) => {
            const isActive = activeFilter === filter;
            const Icon = filter === 'Images' ? ImageIcon : filter === 'Videos' ? Video : Grid3X3;
            return (
              <motion.button
                key={filter}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.96 }}
                onClick={() => setActiveFilter(filter)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-full text-sm tracking-wide transition-all duration-300 border ${
                  isActive
                    ? 'bg-white text-black border-white'
                    : 'bg-transparent text-white/60 border-white/20 hover:border-white/50 hover:text-white'
                }`}
              >
                <Icon size={14} />
                {filter}
              </motion.button>
            );
          })}

          {/* Count badge */}
          <span className="text-white/30 text-sm ml-2">
            {filteredCreatives.length} creative{filteredCreatives.length !== 1 ? 's' : ''}
          </span>
        </motion.div>

        {/* ── Loading Skeleton ─────────────────────────────────────────────── */}
        {loading && (
          <div style={{ columnCount: 4, columnGap: '16px' }} className="masonry-grid">
            {Array.from({ length: 12 }).map((_, i) => (
              <div
                key={i}
                className="mb-4 break-inside-avoid rounded-xl overflow-hidden bg-white/5 animate-pulse"
                style={{ height: `${180 + (i % 3) * 80}px` }}
              />
            ))}
          </div>
        )}

        {/* ── Masonry Grid (CSS columns) ───────────────────────────────────── */}
        {!loading && (
          <h2 className="sr-only">AI creatives gallery grid</h2>
        )}
        {!loading && <AnimatePresence mode="wait">
          <motion.div
            key={activeFilter}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            style={{
              columnCount: 4,
              columnGap: '16px',
            }}
            className="masonry-grid"
          >
            <style>{`
              @media (max-width: 1024px) { .masonry-grid { column-count: 3 !important; } }
              @media (max-width: 768px)  { .masonry-grid { column-count: 2 !important; } }
              @media (max-width: 480px)  { .masonry-grid { column-count: 1 !important; } }
            `}</style>
            {filteredCreatives.map((item, index) => (
              <MasonryItem
                key={item.id}
                item={item}
                index={index}
                onSelect={handleSelect}
              />
            ))}
          </motion.div>
        </AnimatePresence>}

      </div>

      {/* ── Contextual cross-links (Portfolio + Contact) ────────────────────── */}
      <section className="relative px-6 py-16">
        <p className="text-white/70 text-sm md:text-base max-w-2xl mx-auto text-center">
          Want to see how these visuals ship for real clients?{' '}
          <Link
            to="/portfolio"
            title="View our AI brand identity case studies"
            className="text-violet-300 hover:text-white underline underline-offset-4 decoration-violet-400/40 hover:decoration-violet-300 transition-colors"
          >
            View our AI brand identity case studies
          </Link>{' '}
          or{' '}
          <Link
            to="/contact"
            title="Book a free AI creative audit"
            className="text-violet-300 hover:text-white underline underline-offset-4 decoration-violet-400/40 hover:decoration-violet-300 transition-colors"
          >
            book a free AI creative audit
          </Link>
          .
        </p>
      </section>

      {/* ── FAQ ─────────────────────────────────────────────────────────────── */}
      <FAQ
        faqs={aiCreativesFaqs}
        title="About the AI creatives gallery"
        id="creatives-faq"
        schemaKey="creatives-faq"
      />

      {/* ── Lightbox ────────────────────────────────────────────────────────── */}
      {selectedItem && (
        <Lightbox item={selectedItem} onClose={handleClose} />
      )}
    </div>
  );
};

export default AICreatives;
